import { create } from 'zustand';
import { type ThemeConfig, type ThemeColors, themes, getTheme, DEFAULT_THEME_ID } from '../config/themes';

// Mode d'affichage choisi par l'utilisateur (persisté). 'system' suit l'OS.
type Mode = 'light' | 'dark' | 'system';
// Mode effectif après résolution de 'system' et de la compatibilité du thème.
type ResolvedMode = 'light' | 'dark';

interface ThemeState {
  themeId: string;
  theme: ThemeConfig;
  mode: Mode; // choix utilisateur, persisté (clé 'splinity-mode')
  resolvedMode: ResolvedMode; // effectif après résolution de 'system'
  setTheme: (id: string) => void;
  setMode: (m: Mode) => void;
}

const STORAGE_KEY = 'splinity-theme';
const MODE_STORAGE_KEY = 'splinity-mode';

// Migration transparente : un id pointant vers un thème retiré (prune, volt…)
// retombe sur DEFAULT_THEME_ID. On normalise l'id ICI — sinon le store garderait
// `themeId: 'prune'` avec `theme: meridian`, désynchronisant tout ce qui teste
// l'id (visages conditionnels, état actif du sélecteur de thèmes).
function loadThemeId(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && themes[stored] ? stored : DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

function loadMode(): Mode {
  try {
    const value = localStorage.getItem(MODE_STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
    return 'system';
  } catch {
    return 'system';
  }
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

// Résout le mode effectif. Compat 8→3 thèmes : un thème sans `colorsDark`
// (Classic, Aurora) est TOUJOURS forcé en clair — il ne subit aucun sombre.
function resolveMode(mode: Mode, theme: ThemeConfig): ResolvedMode {
  if (!theme.colorsDark) return 'light';
  if (mode === 'system') return prefersDark() ? 'dark' : 'light';
  return mode;
}

function applyTheme(theme: ThemeConfig, resolvedMode: ResolvedMode) {
  const root = document.documentElement;

  // Palette effective : en sombre, la surcharge `colorsDark` prime sur `colors`.
  const palette: Partial<ThemeColors> =
    resolvedMode === 'dark' && theme.colorsDark
      ? { ...theme.colors, ...theme.colorsDark }
      : theme.colors;

  // Apply colors as CSS variables
  for (const [key, value] of Object.entries(palette)) {
    if (value === undefined) continue;
    root.style.setProperty(`--color-${key}`, value);
    // Also set RGB version for Tailwind opacity support (only for hex values)
    if (value.startsWith('#')) {
      root.style.setProperty(`--color-${key}-rgb`, hexToRgb(value));
    } else {
      // Valeur non-hex (rgba…) → retirer un éventuel -rgb devenu obsolète.
      root.style.removeProperty(`--color-${key}-rgb`);
    }
  }

  // Apply fonts
  root.style.setProperty('--font-heading', theme.font.heading);
  root.style.setProperty('--font-body', theme.font.body);

  // Apply style tokens
  root.style.setProperty('--radius', theme.style.borderRadius);
  root.style.setProperty('--radius-lg', theme.style.borderRadiusLg);
  root.style.setProperty('--radius-xl', theme.style.borderRadiusXl);
  root.style.setProperty('--radius-full', theme.style.borderRadiusFull);
  root.style.setProperty('--shadow', theme.style.shadow);
  root.style.setProperty('--shadow-md', theme.style.shadowMd);
  root.style.setProperty('--shadow-lg', theme.style.shadowLg);

  // Apply layout data attributes (for CSS-based layout switching)
  root.dataset.card = theme.layout.cardStyle;
  root.dataset.button = theme.layout.buttonShape;
  root.dataset.input = theme.layout.inputStyle;
  root.dataset.nav = theme.layout.navStyle;
  root.dataset.sidebar = theme.layout.sidebarStyle;
  root.dataset.icon = theme.layout.iconShape;
  root.dataset.density = theme.layout.density;
  root.dataset.header = theme.layout.headerStyle;
  root.dataset.pagebg = theme.layout.pageBackground;
  root.dataset.statcard = theme.layout.statCardStyle || 'standard';
  root.dataset.table = theme.layout.tableStyle || 'standard';

  // Mode effectif : pilote le CSS sombre (grain, scrollbars, ombres, color-scheme)
  root.dataset.mode = resolvedMode;
  // Id du thème : permet un CSS spécifique à un thème (ex. le grain de Méridien)
  // sans toucher aux autres thèmes.
  root.dataset.themeId = theme.id;

  // Load Google Fonts if needed
  loadGoogleFont(theme);
}

function loadGoogleFont(theme: ThemeConfig) {
  const existingLink = document.getElementById('theme-google-font');
  if (existingLink) {
    existingLink.remove();
  }

  if (theme.font.googleFontsUrl) {
    const link = document.createElement('link');
    link.id = 'theme-google-font';
    link.rel = 'stylesheet';
    link.href = theme.font.googleFontsUrl;
    document.head.appendChild(link);
  }
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

// Apply initial theme immediately (before React renders) — lit aussi le mode
// pour éviter tout flash clair→sombre au chargement.
const initialThemeId = loadThemeId();
const initialTheme = getTheme(initialThemeId);
const initialMode = loadMode();
const initialResolvedMode = resolveMode(initialMode, initialTheme);
applyTheme(initialTheme, initialResolvedMode);

export const useThemeStore = create<ThemeState>((set, get) => ({
  themeId: initialThemeId,
  theme: initialTheme,
  mode: initialMode,
  resolvedMode: initialResolvedMode,
  setTheme: (id: string) => {
    // Normalisation : un id inconnu retombe sur le défaut, et c'est CET id
    // normalisé qui est persisté et stocké (jamais un id fantôme).
    const themeId = themes[id] ? id : DEFAULT_THEME_ID;
    const theme = getTheme(themeId);
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch {
      // localStorage indisponible — le thème reste appliqué pour la session.
    }
    const resolvedMode = resolveMode(get().mode, theme);
    applyTheme(theme, resolvedMode);
    set({ themeId, theme, resolvedMode });
  },
  setMode: (mode: Mode) => {
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage indisponible — le mode reste appliqué pour la session.
    }
    const { theme } = get();
    const resolvedMode = resolveMode(mode, theme);
    applyTheme(theme, resolvedMode);
    set({ mode, resolvedMode });
  },
}));

// Écoute du changement de préférence OS : recalcule et ré-applique quand
// l'utilisateur est en mode 'system'.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    const { mode, theme } = useThemeStore.getState();
    if (mode !== 'system') return;
    const resolvedMode = resolveMode('system', theme);
    applyTheme(theme, resolvedMode);
    useThemeStore.setState({ resolvedMode });
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange); // Safari < 14
}
